/*
  PTP kit grid (October 2026). Front-end only.
  Draws every Shopify Bundles app bundle from its own pieces: shop and search cards, quick view,
  the product page (grid as the first photo, chips, "In the bundle" list, bought separately panel)
  and the football page bundle cards. The pieces, quantities and prices are read from the store's
  public Storefront API, so a bundle gets its grid as soon as it is published, and follows any change
  made in the Bundles app. Nothing is written anywhere.
  Rules: grid only for bundles of 2 or more different products (one-product bundles keep their photo);
  pieces in price order, highest first; x2 badge when a bundle has several of one piece; 1 to 9 pieces
  use the fixed layouts, 10 to 12 four by three, 13 to 16 four by four, spare cells become double tiles;
  wide or tall frames pick the closest grid. A piece that is not on the Online Store can't show.
  The bundle's own photo stays until the piece photos have loaded, then the grid fades in over it.
  Switch off in Theme settings > PTP 2027 > Bundles.
*/
(function () {
  'use strict';
  if (window.PTPKit || window.PTPKitOff) return;

  var API = '/api/2026-10/graphql.json';
  var STORE_KEY = 'ptpKit:v2';
  var TTL = 10 * 60 * 1000;
  var CHUNK = 20;
  var WAIT_IMG = 1500;   // longest wait for the piece photos before swapping a photo that is on screen
  var DPR = Math.min(window.devicePixelRatio || 1, 2);
  var data = {};      // product id -> parsed bundle, or false when not a bundle
  var stamp = {};     // product id -> time fetched
  var waiting = {};   // product id -> [callbacks]
  var queued = [];
  var timer = null;

  /* ---------- helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var fmt = null;
  function money(n) {
    try {
      if (!fmt) fmt = new Intl.NumberFormat('en-AU', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD' });
      return fmt.format(n).replace(/^A\$/, '$');
    } catch (e) { return '$' + n.toFixed(2); }
  }
  var keep = { 'X': 'X', 'XL': 'XL', 'XXL': 'XXL', 'S': 'S', 'M': 'M', 'L': 'L', 'PTP': 'PTP', 'TRG': 'TRG', 'UNISEX': 'Unisex', 'PAIR': 'Pair', 'LB': 'lb', 'KG': 'kg', 'OF': 'of', 'AND': 'and', 'THE': 'the', 'WITH': 'with', 'AU': 'AU', 'MYOSTICK': 'MyoStick' };
  function titleCase(str) {
    if (!/[A-Z]{3}/.test(str) || /[a-z]/.test(str)) return str;
    return str.replace(/[A-Z0-9][A-Z0-9'’\.\/"”+]*/g, function (w, i) {
      if (keep[w] !== undefined) return (i === 0 && keep[w] === keep[w].toLowerCase() && w.length > 2) ? w.charAt(0) + w.slice(1).toLowerCase() : keep[w];
      if (/\d/.test(w)) return w;
      return w.charAt(0) + w.slice(1).toLowerCase();
    });
  }
  function gid(id) { return 'gid://shopify/Product/' + id; }
  function num(id) { return String(id).replace(/\D/g, ''); }
  function parsing() { return document.readyState === 'loading'; }
  function child(el, cls) {
    for (var c = el.firstElementChild; c; c = c.nextElementSibling) if (c.classList.contains(cls)) return c;
    return null;
  }

  /* ---------- cache (per viewer convenience only) ---------- */
  try {
    localStorage.removeItem('ptpKit:v1');
    var saved = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
    var now0 = Date.now();
    Object.keys(saved).forEach(function (k) {
      if (saved[k] && now0 - saved[k].t < TTL) { data[k] = saved[k].d; stamp[k] = saved[k].t; }
    });
  } catch (e) {}
  function persist() {
    try {
      var out = {};
      Object.keys(data).forEach(function (k) { if (stamp[k]) out[k] = { t: stamp[k], d: data[k] }; });
      localStorage.setItem(STORE_KEY, JSON.stringify(out));
    } catch (e) {}
  }

  /* ---------- Storefront API ---------- */
  var QUERY = 'query PTPKit($ids:[ID!]!){nodes(ids:$ids){... on Product{id availableForSale variants(first:1){nodes{price{amount} availableForSale components(first:30){nodes{quantity productVariant{id price{amount} availableForSale product{id title handle onlineStoreUrl featuredImage{altText t:url(transform:{maxWidth:240}) s:url(transform:{maxWidth:480}) l:url(transform:{maxWidth:1000})}}}}}}}}}}';

  function parse(node) {
    if (!node || !node.variants || !node.variants.nodes.length) return false;
    var v = node.variants.nodes[0];
    var comps = (v.components && v.components.nodes) || [];
    var pieces = [];
    comps.forEach(function (c, i) {
      var pv = c && c.productVariant;
      if (!pv || !pv.product) return;
      var im = pv.product.featuredImage || {};
      pieces.push({
        q: c.quantity || 1,
        title: titleCase(pv.product.title || ''),
        url: pv.product.onlineStoreUrl ? pv.product.onlineStoreUrl.replace(/^https?:\/\/[^/]+/, '') : '/products/' + pv.product.handle,
        t: im.t || im.s || '',
        s: im.s || '',
        l: im.l || im.s || '',
        price: parseFloat(pv.price && pv.price.amount) || 0,
        avail: pv.availableForSale !== false,
        i: i
      });
    });
    if (!pieces.length) return false;
    pieces.sort(function (a, b) { return (b.price - a.price) || (a.i - b.i); });
    var count = 0, sum = 0;
    pieces.forEach(function (p) { count += p.q; sum += p.price * p.q; });
    var price = parseFloat(v.price && v.price.amount) || 0;
    var save = Math.round((sum - price) * 100) / 100;
    return {
      pieces: pieces,
      count: count,
      kinds: pieces.length,
      sum: Math.round(sum * 100) / 100,
      price: price,
      save: save > 0.009 ? save : 0,
      avail: v.availableForSale !== false && node.availableForSale !== false
    };
  }

  function flush() {
    timer = null;
    var ids = queued.splice(0);
    for (var i = 0; i < ids.length; i += CHUNK) fetchChunk(ids.slice(i, i + CHUNK));
  }
  function fetchChunk(ids) {
    fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ query: QUERY, variables: { ids: ids.map(gid) } })
    }).then(function (r) { return r.json(); }).then(function (res) {
      var nodes = (res && res.data && res.data.nodes) || [];
      var t = Date.now();
      ids.forEach(function (id, i) {
        data[id] = parse(nodes[i]);
        stamp[id] = t;
        done(id);
      });
      persist();
    }).catch(function () {
      ids.forEach(function (id) { data[id] = false; done(id); });
    });
  }
  function done(id) {
    var cbs = waiting[id] || [];
    delete waiting[id];
    cbs.forEach(function (cb) { try { cb(data[id]); } catch (e) { if (window.console) console.warn('PTP kit grid', e); } });
  }
  function get(id, cb) {
    id = num(id);
    if (!id) return;
    if (data.hasOwnProperty(id)) { cb(data[id]); return; }
    if (waiting[id]) { waiting[id].push(cb); return; }
    waiting[id] = [cb];
    queued.push(id);
    if (!timer) timer = setTimeout(flush, 30);
  }

  /* ---------- layouts ---------- */
  function squareLayout(n) {
    if (n <= 9) return { cls: 'k' + n, dbl: 0, show: n };
    if (n <= 12) return { cls: 'k12', dbl: 12 - n, show: n };
    if (n <= 16) return { cls: 'k16', dbl: 16 - n, show: n };
    return { cls: 'k16', dbl: 0, show: 15, more: n - 15 };
  }
  function freeLayout(n, aspect) {
    var best = null;
    for (var r = 1; r <= 6; r++) {
      for (var c = 1; c <= 8; c++) {
        var cells = c * r;
        if (cells < n) continue;
        var spare = cells - n;
        if (spare > 0 && c < 2) continue;
        if (spare > n) continue;
        var tile = (aspect * r) / c;
        var score = Math.abs(Math.log(tile)) + spare * 0.45 + (c * r > 16 ? 1 : 0);
        if (!best || score < best.score) best = { c: c, r: r, dbl: spare, score: score };
      }
    }
    return best || { c: n, r: 1, dbl: 0 };
  }
  function layoutFor(n, aspect, square) {
    if (square || (aspect > 0.8 && aspect < 1.25) || n > 16) return squareLayout(n);
    var f = freeLayout(n, aspect);
    return { free: true, c: f.c, r: f.r, dbl: f.dbl, show: n };
  }
  /* share of the box width and height that tile i gets, to pick a photo size that is sharp but not heavy */
  function share(L, i) {
    if (L.free) return [(i < L.dbl ? 2 : 1) / L.c, 1 / L.r];
    switch (L.cls) {
      case 'k1': return [1, 1];
      case 'k2': return [0.5, 1];
      case 'k3': return i === 0 ? [0.6, 1] : [0.4, 0.5];
      case 'k4': return [0.5, 0.5];
      case 'k5': return i < 2 ? [0.5, 0.5] : [1 / 3, 0.5];
      case 'k6': return [1 / 3, 0.5];
      case 'k7': return i < 3 ? [1 / 3, 0.5] : [0.25, 0.5];
      case 'k8': return [0.25, 0.5];
      case 'k9': return [1 / 3, 1 / 3];
      case 'k12': return [(i < L.dbl ? 2 : 1) / 4, 1 / 3];
      default: return [(i < L.dbl ? 2 : 1) / 4, 0.25];
    }
  }
  function pick(p, px) {
    if (!px) return p.s || p.l || p.t;
    if (px <= 230) return p.t || p.s;
    if (px <= 470) return p.s || p.l;
    return p.l || p.s;
  }
  /* opt: w and h of the box in px (0 when hidden), square, alt (false = decorative) */
  function gridHTML(kit, opt) {
    var pieces = kit.pieces;
    var w = opt.w || 0, h = opt.h || w;
    var L = layoutFor(pieces.length, w && h ? w / h : 1, opt.square);
    var cls = 'ptp-kit__grid ' + (L.free ? 'ptp-kit__grid--free' : L.cls);
    var style = L.free ? ' style="grid-template-columns:repeat(' + L.c + ',minmax(0,1fr));grid-template-rows:repeat(' + L.r + ',minmax(0,1fr))"' : '';
    var html = '<div class="' + cls + '"' + style + '>';
    var urls = [];
    for (var i = 0; i < L.show; i++) {
      var p = pieces[i];
      var sh = share(L, i);
      var src = pick(p, w ? Math.min(w * sh[0], h * sh[1]) * DPR * 0.9 : 0);
      if (src) urls.push(src);
      html += '<div class="ptp-kit__tile' + (i < L.dbl ? ' is-dbl' : '') + '">' +
        (src ? '<img src="' + esc(src) + '" alt="' + (opt.alt === false ? '' : esc(p.title)) + '" loading="lazy" decoding="async">' : '') +
        (p.q > 1 ? '<span class="ptp-kit__qty">×' + p.q + '</span>' : '') + '</div>';
    }
    if (L.more) html += '<div class="ptp-kit__tile ptp-kit__more"><span>+' + L.more + '</span></div>';
    return { html: html + '</div>', urls: urls };
  }
  function kitBox(kit, opt, extra) {
    var g = gridHTML(kit, opt);
    var el = document.createElement('div');
    el.className = 'ptp-kit' + (extra ? ' ' + extra : '');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = g.html;
    return { el: el, urls: g.urls };
  }
  function piecesLabel(kit) { return kit.count + (kit.count === 1 ? ' piece' : ' pieces'); }

  /* swap straight away when the spot is off screen or hidden (its photos load lazily later);
     when it is on screen, wait for the piece photos first (at most WAIT_IMG) so the grid arrives whole */
  function onScreen(el) {
    if (!el || !el.getClientRects().length) return false;
    var r = el.getBoundingClientRect();
    return r.bottom > -100 && r.top < (window.innerHeight || 800) + 200;
  }
  function whenLoaded(urls, el, cb) {
    if (!urls.length || !onScreen(el)) { cb(); return; }
    var left = urls.length, finished = false;
    var finish = function () { if (!finished) { finished = true; cb(); } };
    urls.forEach(function (u) {
      var im = new Image();
      im.onload = im.onerror = function () { if (--left <= 0) finish(); };
      im.src = u;
    });
    setTimeout(finish, WAIT_IMG);
  }
  /* the photo underneath is hidden once the grid has faded in */
  function shown(el) { setTimeout(function () { el.classList.add('ptp-kit-shown'); }, 260); }

  /* ---------- shop, search and collection cards ---------- */
  function doCard(card) {
    var st = card.dataset.ptpKit;
    if (st === 'yes') { if (card.__ptpKit) ensureCard(card, card.__ptpKit); return; }
    if (st) return;
    card.dataset.ptpKit = 'wait';
    get(card.getAttribute('data-id'), function (kit) {
      if (!kit || kit.kinds < 2) { card.dataset.ptpKit = 'no'; return; }
      var wrap = card.querySelector('.product-card__images-wrapper');
      var inner = card.querySelector('.product-card__inner');
      if (!wrap || !inner || !card.querySelector('.product-card__price')) {
        if (parsing()) { delete card.dataset.ptpKit; return; }   // still arriving: the next scan picks it up
        if (!wrap || !inner) { card.dataset.ptpKit = 'no'; return; }
      }
      var b = kitBox(kit, { square: true, alt: false, w: wrap.clientWidth, h: wrap.clientHeight }, 'ptp-kit--card');
      whenLoaded(b.urls, wrap, function () {
        card.__ptpKit = kit;
        card.dataset.ptpKit = 'yes';
        card.classList.add('ptp-kit-card');
        wrap.appendChild(b.el);
        ensureCard(card, kit);
        shown(card);
      });
    });
  }
  function ensureCard(card, kit) {
    var wrap = card.querySelector('.product-card__images-wrapper');
    var inner = card.querySelector('.product-card__inner');
    if (!wrap || !inner) return;
    if (!child(wrap, 'ptp-kit')) wrap.appendChild(kitBox(kit, { square: true, alt: false, w: wrap.clientWidth, h: wrap.clientHeight }, 'ptp-kit--card').el);
    var label = 'Bundle · ' + piecesLabel(kit);
    if (!inner.querySelector('.ptp-kit-chip')) {
      var badges = inner.querySelector('.product-card__on-image-badges');
      if (badges) badges.insertAdjacentHTML('beforeend', '<span class="ptp-kit-chip ptp-kit-chip--in">' + esc(label) + '</span>');
      else inner.insertAdjacentHTML('beforeend', '<div class="ptp-kit-corner ptp-kit-corner--left"><span class="ptp-kit-chip">' + esc(label) + '</span></div>');
    }
    if (kit.save && kit.avail && !inner.querySelector('.ptp-kit-corner--right')) {
      inner.insertAdjacentHTML('beforeend', '<div class="ptp-kit-corner ptp-kit-corner--right"><span class="ptp-kit-save">Save ' + esc(money(kit.save)) + '</span></div>');
    }
    var price = card.querySelector('.product-card__price');
    if (price && !card.querySelector('.ptp-kit-meta')) {
      price.insertAdjacentHTML('afterend', '<p class="ptp-kit-meta">' +
        (kit.save && kit.avail ? '<span class="ptp-kit-save">Save ' + esc(money(kit.save)) + '</span>' : '') +
        '<span class="ptp-kit-meta__n">' + esc(piecesLabel(kit)) + '</span></p>');
    }
  }

  /* ---------- product page and quick view gallery ---------- */
  function galleryId(pmc) {
    var m = /^product-media-(\d+)-/.exec(pmc.id || '');
    return m ? m[1] : null;
  }
  function doGallery(pmc) {
    if (pmc.dataset.ptpKit) return;
    var id = galleryId(pmc);
    if (!id) { pmc.dataset.ptpKit = 'no'; return; }
    pmc.dataset.ptpKit = 'wait';
    get(id, function (kit) {
      if (!kit || kit.kinds < 2) { pmc.dataset.ptpKit = 'no'; return; }
      insertSlide(pmc, kit, 0, false);
    });
  }
  function parts(pmc) {
    var cc = pmc.querySelector('carousel-component[data-product-media-carousel-component]');
    var cont = cc && cc.querySelector('[data-container="carousel"]');
    return { cc: cc, cont: cont };
  }
  function slideBox(kit, first) {
    return kitBox(kit, { square: true, w: first ? first.clientWidth : 0, h: first ? first.clientHeight : 0 }, 'ptp-kit--pdp');
  }
  function insertSlide(pmc, kit, tries, loaded) {
    var p = parts(pmc);
    var defined = !!(window.customElements && customElements.get('carousel-component'));
    /* wait for the slides to arrive; once the theme's carousel is defined, wait for it to start */
    if (!p.cc || !p.cont || !p.cont.firstElementChild || (defined && !p.cc.embla)) {
      if (tries < 100) setTimeout(function () { insertSlide(pmc, kit, tries + 1, loaded); }, 100);
      else pmc.dataset.ptpKit = 'no';
      return;
    }
    /* before the theme's carousel starts (bundle seen on an earlier page, so its details are cached) the
       grid goes in straight away and the carousel starts with it; after, wait for the photos, then swap */
    if (!loaded && defined) {
      whenLoaded(slideBox(kit, p.cont.firstElementChild).urls, pmc, function () { insertSlide(pmc, kit, tries, true); });
      return;
    }
    pmc.dataset.ptpKit = 'yes';
    pmc.classList.add('ptp-kit-gallery');
    place(pmc, kit);
    /* the theme rebuilds slides when a variant with its own photos is picked: put the grid back */
    try {
      var cont = p.cont;
      new MutationObserver(function () {
        if (!cont.querySelector('.ptp-kit-slide')) setTimeout(function () { if (!cont.querySelector('.ptp-kit-slide')) place(pmc, kit); }, 60);
      }).observe(cont, { childList: true });
    } catch (e) {}
  }
  function place(pmc, kit) {
    var p = parts(pmc);
    var cc = p.cc, cont = p.cont;
    var first = cont && cont.firstElementChild;
    if (!first || cont.querySelector('.ptp-kit-slide')) return;
    var started = !!cc.embla;
    var cur = 0;
    if (started) { try { cur = cc.embla.selectedScrollSnap() || 0; } catch (e) {} }
    var slide = first.cloneNode(true);
    slide.classList.remove('is-selected', 'is-snapped', 'product-media-carousel-mobile__slide--alone');
    slide.classList.add('ptp-kit-slide');
    slide.removeAttribute('data-media-id');
    slide.removeAttribute('data-zoom-cursor-target');
    var box = slide.querySelector('.product-media__image-wrapper') || slide;
    box.innerHTML = '';
    box.appendChild(slideBox(kit, first).el);
    var label = document.createElement('span');
    label.className = 'visually-hidden';
    label.textContent = 'What is in the bundle: ' + kit.pieces.map(function (x) { return (x.q > 1 ? x.q + ' x ' : '') + x.title; }).join(', ');
    box.appendChild(label);
    cont.insertBefore(slide, first);

    var dotsComp = pmc.querySelector('carousel-dots');
    var dots = dotsComp && dotsComp.querySelector('[data-container="dots"]');
    if (dots && dots.firstElementChild && !dots.querySelector('.ptp-kit-dot')) {
      var d0 = dots.firstElementChild;
      var nd = d0.cloneNode(true);
      nd.classList.add('ptp-kit-dot');
      var img = nd.querySelector('img');
      if (img) {
        var holder = img.parentElement;
        holder.innerHTML = '';
        holder.appendChild(kitBox(kit, { square: true, alt: false, w: 90, h: 90 }, 'ptp-kit--thumb').el);
      }
      dots.insertBefore(nd, d0);
      Array.prototype.forEach.call(dots.children, function (el, i) {
        el.setAttribute('data-dot-index', String(i));
        el.classList.toggle('is-primary', i === 0);
      });
      try {
        if (started && dotsComp.embla) dotsComp.embla.destroy();
        else if (!started && dotsComp.embla) dotsComp.embla.reInit();
      } catch (e) {}
    }
    /* stay on the photo the shopper is looking at; otherwise show the grid first */
    var to = cur > 0 ? cur + 1 : 0;
    if (started) {
      try {
        cc.embla.reInit();
        cc.embla.scrollTo(to, true);
      } catch (e) {}
    }
    var count = pmc.querySelector('.ptp-pdp-gal__count');
    if (count) {
      var last = count.lastChild;
      if (last && last.nodeType === 3) last.nodeValue = ' / ' + cont.children.length;
      var n = count.querySelector('[data-ptp-gal-n]');
      if (n) n.textContent = String(to + 1);
    }
  }

  /* product pages and quick view set to a stacked or grid media layout (no carousel) */
  function doMediaList(pm) {
    if (pm.dataset.ptpKit) return;
    var id = galleryId(pm);
    if (!id) { pm.dataset.ptpKit = 'no'; return; }
    pm.dataset.ptpKit = 'wait';
    get(id, function (kit) {
      var first = pm.querySelector('[data-media-id]');
      if (!kit || kit.kinds < 2) { pm.dataset.ptpKit = 'no'; return; }
      if (!first || first.parentElement !== pm) {
        if (parsing()) delete pm.dataset.ptpKit; else pm.dataset.ptpKit = 'no';
        return;
      }
      var b = kitBox(kit, { square: true, w: first.clientWidth, h: first.clientHeight }, 'ptp-kit--pdp');
      whenLoaded(b.urls, pm, function () {
        pm.dataset.ptpKit = 'yes';
        var item = first.cloneNode(true);
        item.classList.add('ptp-kit-slide');
        item.removeAttribute('data-media-id');
        item.removeAttribute('data-zoom-cursor-target');
        var box = item.querySelector('.product-media__image-wrapper') || item;
        box.innerHTML = '';
        box.appendChild(b.el);
        pm.insertBefore(item, first);
      });
    });
  }

  /* ---------- product page and quick view text ---------- */
  function doInfo(block) {
    var st = block.dataset.ptpKit;
    if (st === 'yes') { if (block.__ptpKit) info(block, block.__ptpKit); return; }
    if (st) return;
    var pb = block.querySelector('product-block[id^="product-price-"]');
    var m = pb && /^product-price-(\d+)-/.exec(pb.id);
    if (!m) { if (!parsing()) block.dataset.ptpKit = 'no'; return; }
    block.dataset.ptpKit = 'wait';
    get(m[1], function (kit) {
      if (!kit || kit.kinds < 2) { block.dataset.ptpKit = 'no'; return; }
      block.__ptpKit = kit;
      block.dataset.ptpKit = 'yes';
      info(block, kit);
    });
  }
  var APP_CHIP = '<span class="ptp-kit-chip ptp-kit-chip--app">Program in the PTP app</span>';
  var APP_ROW = '<li class="ptp-kit-list__row ptp-kit-list__row--app"><span class="ptp-kit-list__pic ptp-kit-list__app">App</span>' +
    '<span class="ptp-kit-list__name">Training program in the PTP app</span><span class="ptp-kit-list__price">Included</span></li>';
  /* adds whatever is missing, so it also survives the theme redrawing the price */
  function info(block, kit) {
    var root = block.closest('.product__info') || block.parentElement;
    if (!root) return;
    root.classList.add('ptp-kit-info');
    var desc = root.querySelector('.custom-pdp-desctip, .product-description, .product-content-tab');
    var hasApp = desc ? /PTP\s+(Training\s+)?App/i.test(desc.textContent) : false;

    var chips = root.querySelector('.ptp-kit-chips');
    var title = root.querySelector('.product-general-info-block > h1, .product-general-info-block > h2, .product-general-info-block .h5');
    if (!chips && title) {
      title.insertAdjacentHTML('beforebegin', '<p class="ptp-kit-chips"><span class="ptp-kit-chip">Bundle · ' + esc(piecesLabel(kit)) + '</span>' + (hasApp ? APP_CHIP : '') + '</p>');
    } else if (chips && hasApp && !chips.querySelector('.ptp-kit-chip--app')) {
      chips.insertAdjacentHTML('beforeend', APP_CHIP);
    }
    if (kit.save && kit.avail && !block.querySelector('.ptp-kit-save')) {
      block.insertAdjacentHTML('beforeend', '<span class="ptp-kit-save ptp-kit-save--pdp">Save ' + esc(money(kit.save)) + '</span>');
    }
    var list = block.nextElementSibling;
    if (list && list.hasAttribute('data-ptp-kit-list')) {
      var ul = list.querySelector('.ptp-kit-list__rows');
      if (hasApp && ul && !ul.querySelector('.ptp-kit-list__row--app')) ul.insertAdjacentHTML('beforeend', APP_ROW);
      return;
    }
    var rows = kit.pieces.map(function (p) {
      return '<li class="ptp-kit-list__row">' +
        '<a class="ptp-kit-list__pic" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' + (p.t ? '<img src="' + esc(p.t) + '" alt="" loading="lazy" decoding="async">' : '') + '</a>' +
        '<a class="ptp-kit-list__name" href="' + esc(p.url) + '">' + (p.q > 1 ? '<span class="ptp-kit-list__q">' + p.q + ' ×</span> ' : '') + esc(p.title) + '</a>' +
        '<span class="ptp-kit-list__price">' + esc(money(p.price * p.q)) + '</span></li>';
    }).join('') + (hasApp ? APP_ROW : '');
    var panel = '';
    if (kit.save) {
      panel = '<dl class="ptp-kit-panel">' +
        '<div><dt>Bought separately</dt><dd>' + esc(money(kit.sum)) + '</dd></div>' +
        '<div><dt>Bundle price</dt><dd>' + esc(money(kit.price)) + '</dd></div>' +
        '<div class="ptp-kit-panel__save"><dt>You save</dt><dd>' + esc(money(kit.save)) + '</dd></div></dl>';
    }
    block.insertAdjacentHTML('afterend', '<div class="ptp-kit-list" data-ptp-kit-list>' +
      '<p class="ptp-kit-list__head">In the bundle</p><ul class="ptp-kit-list__rows" role="list">' + rows + '</ul>' + panel + '</div>');
  }

  /* ---------- football page cards and bands (any element with data-ptp-kit-id) ---------- */
  function doFrame(el) {
    var st = el.dataset.ptpKit;
    if (st === 'yes') { if (el.__ptpKit && !child(el, 'ptp-kit')) el.insertBefore(frameBox(el, el.__ptpKit).el, el.firstChild); return; }
    if (st) return;
    el.dataset.ptpKit = 'wait';
    get(el.getAttribute('data-ptp-kit-id'), function (kit) {
      if (!kit || kit.kinds < 2) { el.dataset.ptpKit = 'no'; return; }
      var b = frameBox(el, kit);
      whenLoaded(b.urls, el, function () {
        el.__ptpKit = kit;
        el.dataset.ptpKit = 'yes';
        el.classList.add('ptp-kit-frame');
        /* first child, so the frame's own chips (Sold out) stay on top */
        el.insertBefore(b.el, el.firstChild);
        shown(el);
      });
    });
  }
  function frameBox(el, kit) {
    var w = el.clientWidth || 3, h = el.clientHeight || 2;
    return kitBox(kit, { w: w, h: h }, 'ptp-kit--frame' + (el.hasAttribute('data-ptp-kit-dark') ? ' ptp-kit--dark' : ''));
  }

  /* ---------- scan ---------- */
  function scan(root) {
    root = root || document;
    if (root.nodeType !== 1 && root !== document) return;
    var q = function (sel) { return root.querySelectorAll ? root.querySelectorAll(sel) : []; };
    Array.prototype.forEach.call(q('product-card[data-id]'), doCard);
    Array.prototype.forEach.call(q('product-media-carousel[id^="product-media-"]'), doGallery);
    Array.prototype.forEach.call(q('product-media[id^="product-media-"]'), doMediaList);
    Array.prototype.forEach.call(q('.block-price'), doInfo);
    Array.prototype.forEach.call(q('[data-ptp-kit-id]'), doFrame);
    /* bag: piece names arrive in capitals from the catalogue */
    Array.prototype.forEach.call(q('[data-ptp-kit-name]:not([data-ptp-cased])'), function (el) {
      el.setAttribute('data-ptp-cased', '1');
      el.textContent = titleCase(el.textContent.trim());
    });
  }
  var pend = null;
  function later() { if (pend) return; pend = setTimeout(function () { pend = null; scan(document); }, 120); }

  function start() {
    scan(document);
    try {
      new MutationObserver(function (list) {
        for (var i = 0; i < list.length; i++) {
          var added = list[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType === 1 && !(n.classList && (n.classList.contains('ptp-kit') || n.classList.contains('ptp-kit-slide') || n.classList.contains('ptp-kit-dot')))) { later(); return; }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
    if (parsing()) document.addEventListener('DOMContentLoaded', function () { scan(document); });
  }

  window.PTPKit = { scan: scan, get: get, version: '2026-10-22b' };
  /* loaded with async: start as soon as the page body exists; the observer picks up the rest as it arrives */
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
})();
